'use client';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

// Edit freely. Every line-1 × line-2 combination should read sensibly, because
// only ONE line changes at a time (so each intermediate state must work).
const LINE_1 = ['Digitální preciznost,', 'Čistý kód,', 'Rychlé načítání,', 'Moderní design,'];
const LINE_2 = ['webová řešení', 'na míru', 'bez kompromisů', 'pro váš růst'];

const LINE_2_CLASS =
  'text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500';

const EASE = [0.23, 1, 0.32, 1] as const;

// "select & paste" — like selecting text in an editor and pasting over it.
// On exit, a highlight sweeps in from the left over the outgoing word (select);
// the word is then replaced instantly and the highlight vanishes instantly
// (no shrink). Implemented via variant propagation: the parent carries the
// exit duration, the overlay child grows on exit and stays at 0 on enter.
const SEL_BG = 'bg-indigo-500/50';
const SEL_PARENT = {
  enter: { opacity: 1 },
  center: { opacity: 1 },
  exit: { opacity: 1, transition: { duration: 0.6 } },
};
const SEL_OVERLAY = {
  enter: { width: '0%' },
  center: { width: '0%' },
  exit: { width: '100%', transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } },
};

// Style pool with weights (how often each is picked).
const STYLES = { slide: 3, defocus: 3, wipe: 2, glide: 2, selectpaste: 2 } as const;
type Style = keyof typeof STYLES;
const POOL = Object.keys(STYLES) as Style[];

function pickVariant(): Style {
  const total = POOL.reduce((s, k) => s + STYLES[k], 0);
  let r = Math.random() * total;
  for (const k of POOL) {
    r -= STYLES[k];
    if (r <= 0) return k;
  }
  return 'slide';
}

/**
 * Build the enter/animate/exit motion for a change.
 * `dir` is +1 for the bottom line, -1 for the top line, so a line always
 * travels toward its OUTER edge (top line up, bottom line down). That means a
 * changing line can never cross into the other line's vertical space — no
 * transient descender/diacritic collisions (e.g. the comma meeting the ů ring).
 */
function buildMotion(style: Style, dir: number, reduce: boolean | null) {
  if (reduce) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0.4 } },
      exit: { opacity: 0, transition: { duration: 0.2 } },
    };
  }
  // Exit always heads toward the outer edge too.
  const exit = { opacity: 0, y: 10 * dir, transition: { duration: 0.25, ease: 'easeIn' as const } };
  switch (style) {
    case 'slide':
      return {
        initial: { opacity: 0, y: 22 * dir },
        animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
        exit,
      };
    case 'defocus':
      return {
        initial: { opacity: 0, filter: 'blur(10px)' },
        animate: { opacity: 1, filter: 'blur(0px)', transition: { duration: 0.55, ease: EASE } },
        exit,
      };
    case 'wipe':
      // Negative top/bottom inset keeps the clip region taller than the text,
      // so descenders (g) and top diacritics (Č, ů) are never clipped; only the
      // right edge animates for the left→right reveal.
      return {
        initial: { opacity: 1, clipPath: 'inset(-45% 100% -45% 0)' },
        animate: { opacity: 1, clipPath: 'inset(-45% 0% -45% 0)', transition: { duration: 0.5, ease: EASE } },
        exit,
      };
    case 'glide':
      return {
        initial: { opacity: 0, x: 26 },
        animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE } },
        exit,
      };
    default:
      return { initial: { opacity: 0 }, animate: { opacity: 1 }, exit };
  }
}

type LineState = { idx: number; variant: Style; key: number };

export default function RotatingHeader() {
  const reduce = useReducedMotion();
  const [l1, setL1] = useState<LineState>({ idx: 0, variant: 'slide', key: 0 });
  const [l2, setL2] = useState<LineState>({ idx: 0, variant: 'defocus', key: 0 });
  const turn = useRef<1 | 2>(1); // which line changes next (alternating)

  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;
    const schedule = () => {
      id = setTimeout(() => {
        if (turn.current === 1) {
          setL1((p) => ({ idx: (p.idx + 1) % LINE_1.length, variant: pickVariant(), key: p.key + 1 }));
          turn.current = 2;
        } else {
          setL2((p) => ({ idx: (p.idx + 1) % LINE_2.length, variant: pickVariant(), key: p.key + 1 }));
          turn.current = 1;
        }
        schedule();
      }, 2800 + Math.random() * 1000);
    };
    schedule();
    return () => clearTimeout(id);
  }, []);

  const renderLine = (state: LineState, options: string[], className: string, dir: number) => {
    const text = options[state.idx];
    let child: React.ReactNode;
    if (!reduce && state.variant === 'selectpaste') {
      child = (
        <motion.span
          key={state.key}
          className={`relative inline-block ${className}`}
          variants={SEL_PARENT}
          initial="enter"
          animate="center"
          exit="exit"
        >
          <motion.span
            aria-hidden
            className={`absolute inset-y-0 left-0 z-0 ${SEL_BG}`}
            variants={SEL_OVERLAY}
            style={{ borderRadius: '1px' }}
          />
          <span className="relative z-10">{text}</span>
        </motion.span>
      );
    } else {
      const m = buildMotion(state.variant, dir, reduce);
      child = (
        <motion.span
          key={state.key}
          className={`inline-block ${className}`}
          initial={m.initial}
          animate={m.animate}
          exit={m.exit}
        >
          {text}
        </motion.span>
      );
    }
    return (
      <span className="block">
        <AnimatePresence mode="wait" initial={false}>
          {child}
        </AnimatePresence>
      </span>
    );
  };

  return (
    <>
      {/* dir = -1 → top line travels up; dir = +1 → bottom line travels down */}
      {renderLine(l1, LINE_1, 'text-white', -1)}
      {renderLine(l2, LINE_2, LINE_2_CLASS, 1)}
    </>
  );
}
